import { estCategorieDossier, type CategorieDossier } from '@repere/core';
import { useRouterState } from '@tanstack/react-router';

/**
 * Le dossier ouvert, lu depuis le routeur.
 *
 * Le menu de l'en-tête porte désormais l'urgence, et l'urgence se dépose **dans un
 * dossier** : il lui faut donc savoir lequel. Un contexte React ne pouvait pas le lui
 * dire — la coquille est un ancêtre de l'écran de dossier, et un contexte ne remonte
 * pas. Le routeur, lui, est au-dessus des deux et connaît déjà la réponse.
 *
 * On interroge les correspondances plutôt que le chemin : `/dossiers/creer` a la même
 * forme qu'un identifiant, et une expression régulière aurait fini par proposer d'alerter
 * l'autre parent depuis l'écran de création.
 */
export const ROUTE_DOSSIER = '/dossiers/$dossierId';

export function useDossierActif(): string | null {
  return useRouterState({
    select: (etat) => {
      const correspondance = etat.matches.find((match) => match.routeId === ROUTE_DOSSIER);
      const params = correspondance?.params as { dossierId?: string } | undefined;
      return params?.dossierId ?? null;
    },
  });
}

/**
 * La catégorie ouverte du dossier, ou `null` hors d'un dossier.
 *
 * Le menu de l'en-tête porte désormais deux catégories que la barre du bas ne montre plus
 * sur téléphone — le journal et les enfants. Il lui faut donc savoir laquelle est ouverte :
 * une entrée de menu qui mène là où l'on est déjà doit le dire, sans quoi rien à l'écran
 * ne répond plus à « où suis-je ».
 *
 * Même règle de repli que la route : une vue absente ou inconnue, c'est « Aujourd'hui ».
 * La lire ici plutôt que de la recevoir en propriété évite de la faire traverser la
 * coquille, qui n'a rien à en faire.
 */
export function useCategorieActive(): CategorieDossier | null {
  return useRouterState({
    select: (etat) => {
      const correspondance = etat.matches.find((match) => match.routeId === ROUTE_DOSSIER);
      if (!correspondance) return null;
      const recherche = correspondance.search as { vue?: string } | undefined;
      const vue = recherche?.vue;
      return vue !== undefined && estCategorieDossier(vue) ? vue : 'aujourdhui';
    },
  });
}
